import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Risk level mappings for filter labels. Keys are the numeric bucket values
 * stored on the ES `*.calculated_value` fields. `0` is the environment-only
 * "Aucun" level (health symbols start at 1, so the 0 entry is harmless for
 * those filters).
 */
const RISK_LEVEL_MAPPING = {
  0: 'common|legends.environment.none',
  1: 'active-substance|risk.Low',
  2: 'active-substance|risk.Light',
  3: 'active-substance|risk.Moderate',
  4: 'active-substance|risk.High',
  5: 'active-substance|risk.Extremely High',
};

/**
 * Risk filter keys that should use risk level mapping
 */
const RISK_FILTER_KEYS = [
  'acute_toxicity',
  'long_term_effects',
  'fish_or_daphnia',
  'birds',
  'bees',
  'persistence',
  'leaching_potential',
];

/**
 * Boolean filter keys that display true/false as Oui/Non.
 */
const BOOLEAN_FILTER_KEYS = ['organic_approval'];

const BOOLEAN_LABEL_MAPPING = {
  true: 'common|Yes',
  false: 'common|No',
};

/**
 * Custom hook to manage filter labels in a search application.
 *
 * The returned `labels` dict is pre-populated with the static risk-level
 * labels for every risk filter key so cold-start renders (e.g. shared URLs
 * with `?birds=0`) immediately show "Aucun / Faible / …" instead of the raw
 * numeric bucket value.
 */
export const useFilterLabels = () => {
  const [userLabels, setUserLabels] = useState({});
  const { t } = useTranslation();

  // Static labels for risk levels and booleans, translated up front. Keys
  // are stored as strings so URL-state values (always strings) resolve
  // via the same lookup — e.g. "true" → "Oui", "3" → "Modéré".
  const staticDefaults = useMemo(() => {
    const out = {};
    RISK_FILTER_KEYS.forEach((key) => {
      const perValue = {};
      Object.entries(RISK_LEVEL_MAPPING).forEach(([value, translationKey]) => {
        perValue[String(value)] = t(translationKey);
      });
      out[key] = perValue;
    });
    BOOLEAN_FILTER_KEYS.forEach((key) => {
      const perValue = {};
      Object.entries(BOOLEAN_LABEL_MAPPING).forEach(([value, translationKey]) => {
        perValue[String(value)] = t(translationKey);
      });
      out[key] = perValue;
    });
    return out;
  }, [t]);

  // Merge: user-set labels override the static defaults for that filter.
  const labels = useMemo(() => {
    const merged = { ...staticDefaults };
    Object.entries(userLabels).forEach(([key, entries]) => {
      merged[key] = { ...(merged[key] || {}), ...entries };
    });
    return merged;
  }, [staticDefaults, userLabels]);

  const setFilterLabel = useCallback((filterKey, valueId, label) => {
    setUserLabels((prev) => ({
      ...prev,
      [filterKey]: {
        ...prev[filterKey],
        [valueId]: label,
      },
    }));
  }, []);

  const getFilterLabel = useCallback(
    (filterKey, valueId) => {
      const stored =
        labels[filterKey]?.[valueId] ??
        labels[filterKey]?.[String(valueId)];
      if (stored) return stored;
      return valueId;
    },
    [labels],
  );

  const clearFilterLabels = useCallback(() => {
    setUserLabels({});
  }, []);

  return {
    setFilterLabel,
    getFilterLabel,
    clearFilterLabels,
    labels,
  };
};
